/**
 * The Overlays control (section-overlays spec §7) — where a user says what
 * ELSE is drawn on their cross-section.
 *
 * The panel is deliberately shaped like the section's own LEGEND rather than
 * like a settings form: the same ink, in the same order, against the same
 * name. That is what makes it readable at a glance next to the drawing —
 * "the amber line is Design v4 · Subgrade, and it is drawn" — and it is why
 * the swatch is a 2 px rule and not a filled dot. An overlay is a LINE on a
 * section; a dot would misdescribe it.
 *
 * ONE list and ONE way in (redesign, 2026-09-07). The first version stacked
 * three headed sections — the list, "Add from another run", "Add a surface" —
 * each with its own full-width selects and its own lone Add button, so the
 * panel was two thirds chrome and the same question ("what do I want on this
 * drawing?") was asked twice in two different vocabularies. The route in is
 * now a single search palette over ONE flat list of candidates: every run
 * plane, every named LandXML surface, every point-cloud reduction, named the
 * way the legend will name them. Picking one adds it; there is no Add button
 * to press afterwards and nothing to reset.
 *
 * Everything the user picks is named, never keyed: a row headed
 * `xml:cm9x…:Subgrade` tells a reader nothing about which of their overlays
 * broke. Ids exist only in `onChange`'s payload and in test ids.
 *
 * This component is PURE UI over `settings`. It never writes settings, never
 * calls the server and holds no async state — the dock owns the save, the
 * materialise mutation and the `busy` flag, so the same panel can be mounted
 * anywhere the list is held. The one exception is the LandXML `<Surface>`
 * name fetch, which is a READ of a file the user already owns and is what
 * turns one "Design v4.xml" entry into the surfaces a reader can actually
 * choose between.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  TriangleAlert,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import type { LayerData } from '@/atoms/layers.atom';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  asOverlayTranslate,
  MAX_SECTION_OVERLAYS,
  overlayCandidates,
  overlayLabel,
  overlayLabelText,
  planeDisplayName,
  sectionOverlayId,
  type OverlayState,
  type SectionOverlaySetting,
} from '@/lib/stationVolumes/sectionOverlays';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { DOCK_CONTROL } from '@/components/dock/dockStyles';

import { PC_METHODS, useLandXmlSurfaceNames } from './SurfacePicker';

export interface SectionOverlaysPopoverProps {
  /** The RESULT layer whose section this is — never itself a candidate. */
  layerId: string;
  /** Every layer in the project: the candidate list is derived from it. */
  layers: readonly LayerData[];
  /**
   * This run's `alignment.sourceHash`. Only a run carrying the very same hash
   * is offered: two runs can name the same road and still have been measured
   * over different geometry, and their offset columns would not line up.
   */
  sourceHash: string | null;
  settings: readonly SectionOverlaySetting[];
  /** `overlayStates(...)` — what the file says about each configured overlay. */
  states: Record<string, { state: OverlayState; error?: string | null }>;
  /** A save/materialise is in flight. Blocks Add, nothing else. */
  busy: boolean;
  /**
   * The stamped `settingsHash` does not describe the list on screen — the
   * cloud has not sampled THIS configuration. Half of what makes Re-sample
   * offerable; the other half is any overlay in a `failed` state, which this
   * component can see for itself.
   */
  stale: boolean;
  /** The FULL next list, in the user's own order. */
  onChange: (next: SectionOverlaySetting[]) => void;
  /** Queue a fresh sample for the CURRENT list — no settings change. */
  onResample: () => void;
}

/** One offerable line, already named the way its row will name it. */
interface Candidate {
  id: string;
  label: string;
  draft: Omit<SectionOverlaySetting, 'id' | 'hidden'>;
}

/** The dock's own type scale, kept in one place so no row drifts to 14 px. */
const SMALL = 'text-[10px] leading-4';
const MUTED = `${SMALL} text-slate-500 dark:text-stone-400`;
const ICON_BUTTON =
  'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded text-slate-500 ' +
  'hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-40 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-white';

/**
 * One pickable candidate.
 *
 * `value` is the LABEL and nothing else, so what the user types is matched
 * against what the user can read — cmdk scores the value, and an id in it
 * would make "xml" match every LandXML surface in the project.
 */
const CandidateItem = ({
  candidate,
  added,
  addedText,
  onPick,
}: {
  candidate: Candidate;
  added: boolean;
  addedText: string;
  onPick: (c: Candidate) => void;
}) => (
  <CommandItem
    value={candidate.label}
    disabled={added}
    data-testid={`sv-overlay-add-${candidate.id}`}
    onSelect={() => onPick(candidate)}
    className='gap-2 px-2 py-1.5 text-[11px]'
  >
    <span className='min-w-0 flex-1 truncate' title={candidate.label}>
      {candidate.label}
    </span>
    {added && <span className={`shrink-0 ${MUTED}`}>{addedText}</span>}
  </CommandItem>
);

/**
 * The surfaces of ONE LandXML file, read only once the user has asked for
 * that file (controller ruling, fix round 1).
 *
 * `useLandXmlSurfaceNames` FETCHES AND PARSES THE WHOLE FILE to read a few
 * hundred bytes of `<Surface name=…>`. Mounting one of these per LandXML
 * layer the moment the palette opened downloaded every LandXML file in the
 * project on a click that might well end in a point cloud — so the palette
 * drills IN instead: a LandXML layer is an item that opens its own file, and
 * exactly one of these is ever mounted, after a choice.
 *
 * A LandXML overlay has to name ONE surface (sampling the whole file cuts
 * every surface in it and draws their union), so an unreadable or
 * surface-less file offers nothing to pick rather than a whole-file entry.
 */
const LandXmlSurfaceItems = ({
  layer,
  layerName,
  addedIds,
  addedText,
  loadingText,
  emptyText,
  onPick,
}: {
  layer: LayerData | undefined;
  layerName: string;
  addedIds: ReadonlySet<string>;
  addedText: string;
  loadingText: string;
  emptyText: string;
  onPick: (c: Candidate) => void;
}) => {
  const { names, loading, error } = useLandXmlSurfaceNames(layer);
  if (loading || error || names.length === 0)
    return (
      <div data-testid={`sv-overlay-xml-status-${layer?.id ?? ''}`} className={`px-2 py-1.5 ${MUTED}`}>
        {layerName} › {loading ? loadingText : emptyText}
      </div>
    );
  return (
    <>
      {names.map((name) => {
        const draft = { kind: 'LAYER_SURFACE' as const, layerId: layer?.id ?? '', ref: name, pcMethod: null };
        const id = sectionOverlayId(draft);
        return (
          <CandidateItem
            key={id}
            candidate={{ id, label: `${layerName} › ${name}`, draft }}
            added={addedIds.has(id)}
            addedText={addedText}
            onPick={onPick}
          />
        );
      })}
    </>
  );
};

export const SectionOverlaysPopover = ({
  layerId,
  layers,
  sourceHash,
  settings,
  states,
  busy,
  stale,
  onChange,
  onResample,
}: SectionOverlaysPopoverProps) => {
  const { t } = useTranslation('project');
  const palette = useSvPalette();
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  /** The LandXML layer the palette has drilled INTO, if any. Nothing is
   *  fetched until this is set, and only ever for this one layer. */
  const [drillId, setDrillId] = useState<string | null>(null);
  /** Controlled so drilling in or out starts from a clean search. */
  const [query, setQuery] = useState('');

  /** Every way out of a drilled view goes through here. */
  const surfaceRoot = () => {
    setDrillId(null);
    setQuery('');
  };

  const { runs, surfaces } = useMemo(
    () => overlayCandidates(layers, layerId, sourceHash),
    [layers, layerId, sourceHash],
  );

  const atCap = settings.length >= MAX_SECTION_OVERLAYS;
  const addedIds = useMemo(() => new Set(settings.map((s) => s.id)), [settings]);

  /** The plane/method vocabulary, built once and shared by the row names and
   *  the palette so the two cannot call one plane two things. */
  const labelText = useMemo(() => overlayLabelText(asOverlayTranslate(t)), [t]);

  /**
   * A row's name. The overlays FILE has the authoritative one, but the dock
   * derives the SAME two-part string from the layer registry, so a row keeps
   * its name from the moment it is added — and only falls back to a
   * positional "Overlay 2" when its source layer has left the project.
   */
  const nameOf = (s: SectionOverlaySetting, i: number) =>
    overlayLabel(s, layers, labelText) ?? t('stationVolumes.section.overlays.unnamed', { n: i + 1 });

  /**
   * The two lines of a row, cut out of the ONE name.
   *
   * `overlayLabel` stays the single naming source — the same string the chart
   * legend and the server-written overlays file use, and the same string the
   * remove/hide labels announce. The row merely READS it in two registers:
   * what this line IS (the surface, plane or reduction — the last segment)
   * over where it came from (the layer, and the format it is stored in). A
   * label with no separator (a DXF/GeoJSON file, or the positional fallback
   * for a layer that has left the project) is already just the layer, and
   * keeps the whole string as its first line.
   */
  const splitName = (name: string) => {
    const cut = name.lastIndexOf(' · ');
    return cut < 0 ? { primary: name, layer: '' } : { primary: name.slice(cut + 3), layer: name.slice(0, cut) };
  };

  /**
   * The format one line is stored in, for the second row line.
   *
   * The format names are proper nouns and are written as literals — a
   * "translated" LandXML would be a bug, and the i18n bundles would be lying
   * about having translated it.
   */
  const sourceOf = (s: SectionOverlaySetting): string => {
    if (s.kind === 'RUN_SECTIONS') return t('stationVolumes.section.overlays.kindRun');
    const layer = layers.find((l) => l.id === s.layerId);
    if (!layer) return t('stationVolumes.section.overlays.kindMissing');
    if (layer.type === 'POINTCLOUD') return t('stationVolumes.section.overlays.kindPointcloud');
    if (layer.type === 'VECTOR') return 'GeoJSON';
    const modelType = (layer.metadata as { type?: string } | null)?.type;
    return modelType === 'LANDXML' ? 'LandXML' : modelType === 'DXF' ? 'DXF' : '';
  };

  const stateOf = (s: SectionOverlaySetting): OverlayState => states[s.id]?.state ?? 'pending';

  const stateText = (state: OverlayState) =>
    state === 'drawn'
      ? t('stationVolumes.section.overlays.stateDrawn')
      : state === 'failed'
        ? t('stationVolumes.section.overlays.stateFailed')
        : t('stationVolumes.section.overlays.statePending');

  /** `overlayStates` is pure and has no translator, so a failure with no
   *  reason arrives as `null` rather than as an English sentinel. */
  const reasonOf = (s: SectionOverlaySetting) =>
    states[s.id]?.error?.trim() || t('stationVolumes.section.overlays.failedUnknown');

  /**
   * Every line this section could carry, flattened to what a search can match.
   *
   * Point clouds are listed once per REDUCTION rather than once per file: the
   * reduction is what makes the line, and a picker that offers the cloud and
   * then asks a second question is the two-step form this redesign removed.
   */
  const runCandidates: Candidate[] = useMemo(
    () =>
      runs.flatMap((r) =>
        r.planes.map((plane) => {
          const draft = { kind: 'RUN_SECTIONS' as const, layerId: r.layerId, ref: plane, pcMethod: null };
          return {
            id: sectionOverlayId(draft),
            label: `${r.layerName} › ${planeDisplayName(plane, labelText)}`,
            draft,
          };
        }),
      ),
    [runs, labelText],
  );

  /** Everything in the Surfaces group EXCEPT the LandXML files, which are
   *  drilled into rather than expanded — reading one costs a whole file. */
  const plainSurfaceCandidates: Candidate[] = useMemo(
    () =>
      surfaces.flatMap((s): Candidate[] => {
        if (s.kind === 'LANDXML') return [];
        if (s.kind === 'POINTCLOUD')
          return PC_METHODS.map((m) => {
            const draft = { kind: 'LAYER_SURFACE' as const, layerId: s.layerId, ref: null, pcMethod: m };
            return { id: sectionOverlayId(draft), label: `${s.layerName} › ${labelText.method(m)}`, draft };
          });
        // DXF/GeoJSON name no surface: the file IS the surface.
        const draft = { kind: 'LAYER_SURFACE' as const, layerId: s.layerId, ref: null, pcMethod: null };
        return [{ id: sectionOverlayId(draft), label: s.layerName, draft }];
      }),
    [surfaces, labelText],
  );

  const landXmlSurfaces = surfaces.filter((s) => s.kind === 'LANDXML');
  const anyCandidate = runCandidates.length + plainSurfaceCandidates.length + landXmlSurfaces.length > 0;
  const drilled = landXmlSurfaces.find((s) => s.layerId === drillId) ?? null;

  /**
   * Add one overlay and close the palette.
   *
   * A duplicate is refused IN WORDS. The palette already shows an added line
   * as "Added" and refuses to select it, so this is the belt to that braces —
   * but a silent no-op on the one route that got through would look like a
   * broken palette.
   */
  const add = (c: Candidate) => {
    setPicking(false);
    surfaceRoot();
    if (addedIds.has(c.id)) {
      toast.error(t('stationVolumes.section.overlays.duplicate'));
      return;
    }
    onChange([...settings, { ...c.draft, id: c.id, hidden: false }]);
  };

  /** Hiding is the one edit that must NOT change the settings hash — see
   *  `sectionOverlaysHash`, which excludes `hidden` for exactly this reason. */
  const setHidden = (id: string, hidden: boolean) =>
    onChange(settings.map((s) => (s.id === id ? { ...s, hidden } : s)));

  const remove = (id: string) => onChange(settings.filter((s) => s.id !== id));

  /**
   * Re-sample (spec §7.4 "Refresh") — the way back from a state the user
   * cannot edit their way out of.
   *
   * Every other route to a sample is a SETTINGS CHANGE: add, remove, re-order.
   * So a job that failed, or a stamp left behind by a save whose materialise
   * never landed, had no button at all — the only recourse was to delete a row
   * and add it back, which is a data edit performed to trigger a retry.
   *
   * Present only when there is something to re-sample AND something to fix. A
   * list whose every overlay is drawn against a matching stamp needs no job,
   * and a permanently dead button in the header reads as a broken panel rather
   * than as an unavailable action.
   */
  const anyFailed = settings.some((s) => states[s.id]?.state === 'failed');
  const canResample = settings.length > 0 && (stale || anyFailed);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          data-testid='sv-overlays-button'
          // A tab-bar tool on the Section tab (round-3 spec §B1), so it is the
          // dock's one control size — `DOCK_CONTROL.button`, like View ▾ and
          // Export ▾ beside it — not the 10 px header chip it used to mimic.
          className={DOCK_CONTROL.button}
          title={t('stationVolumes.section.overlays.buttonHelp')}
        >
          <Layers className={DOCK_CONTROL.iconSm} aria-hidden='true' />
          {t('stationVolumes.section.overlays.button')}
          {settings.length > 0 && (
            <span
              data-testid='sv-overlays-count'
              aria-label={t('stationVolumes.section.overlays.badgeLabel', { n: settings.length })}
              className='sv-num ml-0.5 rounded-full bg-slate-200 px-1.5 text-[10px] leading-4 text-slate-700 dark:bg-stone-700 dark:text-stone-100'
            >
              {settings.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* The swatches are painted from `palette.series` in JS, so they need no
          CSS variable — `sv-palette` is here because this content is PORTALLED
          out of the dock's `.sv-root`, and it is the hook that keeps any
          `var(--sv-*)` added inside resolving to the same theme the chart
          uses instead of silently falling back to nothing. */}
      <PopoverContent
        align='end'
        data-testid='sv-overlays-panel'
        aria-labelledby='sv-overlays-title'
        className='sv-palette flex max-h-[70vh] w-90 flex-col overflow-hidden border-slate-200 bg-white/95 p-0 text-[11px] text-slate-700 backdrop-blur dark:border-stone-700 dark:bg-stone-900/95 dark:text-stone-300'
      >
        <div className='flex shrink-0 items-center gap-1 border-b border-slate-200 py-1.5 pr-1.5 pl-3 dark:border-stone-700'>
          <h2
            id='sv-overlays-title'
            data-testid='sv-overlays-title'
            className='flex-1 text-xs font-semibold text-slate-800 dark:text-stone-100'
          >
            {t('stationVolumes.section.overlays.title')}
          </h2>
          {canResample && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  data-testid='sv-overlays-resample'
                  className={ICON_BUTTON}
                  disabled={busy}
                  aria-label={t('stationVolumes.section.overlays.resample')}
                  onClick={onResample}
                >
                  <RefreshCw className='size-3.5' aria-hidden='true' />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('stationVolumes.section.overlays.resampleHelp')}</TooltipContent>
            </Tooltip>
          )}
          <button
            type='button'
            data-testid='sv-overlays-close'
            className={ICON_BUTTON}
            aria-label={t('stationVolumes.section.overlays.close')}
            onClick={() => setOpen(false)}
          >
            <X className='size-3.5' aria-hidden='true' />
          </button>
        </div>

        <ul className='min-h-0 flex-1 overflow-y-auto py-1'>
          {settings.length === 0 ? (
            <li data-testid='sv-overlays-none' className={`px-3 py-2 ${MUTED}`}>
              {t('stationVolumes.section.overlays.none')}
            </li>
          ) : (
            settings.map((s, i) => {
              const name = nameOf(s, i);
              const { primary, layer } = splitName(name);
              const format = sourceOf(s);
              const state = stateOf(s);
              const ink = palette.series[i % palette.series.length];
              return (
                <li
                  key={s.id}
                  data-testid={`sv-overlay-row-${s.id}`}
                  data-hidden={s.hidden ? 'true' : undefined}
                  className='group flex items-center gap-2 px-3 py-1 hover:bg-slate-50 dark:hover:bg-stone-800/60'
                >
                  {/* The ink, as a RULE: this overlay is a line on the
                      drawing, and the index is its place in the FULL list so
                      it matches `overlaySeries` even when one is hidden. A
                      line that is not on the drawing yet is drawn dashed,
                      which is what the reader will see when it arrives. */}
                  <span
                    data-testid={`sv-overlay-ink-${s.id}`}
                    aria-hidden='true'
                    style={
                      state === 'drawn'
                        ? { backgroundColor: ink }
                        : { backgroundImage: `repeating-linear-gradient(to right, ${ink} 0 3px, transparent 3px 6px)` }
                    }
                    className={`h-0.5 w-4 shrink-0 rounded-full ${s.hidden || state !== 'drawn' ? 'opacity-45' : ''}`}
                  />
                  <span className='min-w-0 flex-1'>
                    <span
                      className={`block truncate ${s.hidden ? 'text-slate-400 dark:text-stone-500' : 'text-slate-800 dark:text-stone-100'}`}
                      title={name}
                    >
                      {primary}
                    </span>
                    <span
                      data-testid={`sv-overlay-source-${s.id}`}
                      className={`block truncate ${MUTED}`}
                      title={state === 'failed' ? reasonOf(s) : undefined}
                    >
                      {state === 'failed' ? reasonOf(s) : [layer, format].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span
                    data-testid={`sv-overlay-state-${s.id}`}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 ${SMALL} ${
                      state === 'drawn'
                        ? 'border-slate-200 text-slate-600 dark:border-stone-700 dark:text-stone-400'
                        : state === 'failed'
                          ? 'border-red-300 text-red-700 dark:border-red-900 dark:text-red-300'
                          : 'border-amber-300 text-amber-700 dark:border-amber-900/70 dark:text-amber-300'
                    }`}
                  >
                    {state === 'pending' && (
                      <span
                        aria-hidden='true'
                        className='size-1.5 shrink-0 rounded-full bg-amber-500 motion-safe:animate-pulse'
                      />
                    )}
                    {state === 'failed' && <TriangleAlert className='size-3 shrink-0' aria-hidden='true' />}
                    {stateText(state)}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type='button'
                        data-testid={`sv-overlay-visible-${s.id}`}
                        aria-pressed={!s.hidden}
                        aria-label={t('stationVolumes.section.overlays.visibleLabel', { name })}
                        onClick={() => setHidden(s.id, !s.hidden)}
                        className={ICON_BUTTON}
                      >
                        {s.hidden ? (
                          <EyeOff className='size-3.5' aria-hidden='true' />
                        ) : (
                          <Eye className='size-3.5' aria-hidden='true' />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('stationVolumes.section.overlays.visibleLabel', { name })}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type='button'
                        data-testid={`sv-overlay-remove-${s.id}`}
                        aria-label={t('stationVolumes.section.overlays.removeLabel', { name })}
                        onClick={() => remove(s.id)}
                        className={ICON_BUTTON}
                      >
                        <X className='size-3.5' aria-hidden='true' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('stationVolumes.section.overlays.removeLabel', { name })}</TooltipContent>
                  </Tooltip>
                </li>
              );
            })
          )}
        </ul>

        <div className='flex shrink-0 items-center gap-2 border-t border-slate-200 px-3 py-2 dark:border-stone-700'>
          <Popover
            open={picking}
            onOpenChange={(next) => {
              // At the cap the trigger is aria-disabled rather than disabled,
              // so it stays focusable to announce WHY — but it must not open.
              setPicking(next && !atCap);
              if (!next) surfaceRoot();
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    size='sm'
                    variant='secondary'
                    data-testid='sv-overlays-add'
                    className={`h-7 flex-1 justify-start text-[11px] ${atCap ? 'opacity-50' : ''}`}
                    disabled={busy}
                    // `aria-disabled`, NOT `disabled`: a disabled button is
                    // unfocusable, so the one sentence that explains the dead
                    // control is unreachable from the keyboard — which is
                    // exactly when a user needs it. The click is a no-op
                    // instead (Radix skips its own handler once ours has
                    // called `preventDefault`).
                    aria-disabled={atCap || undefined}
                    title={atCap ? t('stationVolumes.section.overlays.cap', { max: MAX_SECTION_OVERLAYS }) : undefined}
                    onClick={atCap ? (e) => e.preventDefault() : undefined}
                  >
                    {busy ? (
                      <Loader2 data-testid='sv-overlays-busy' className='size-3.5 animate-spin' aria-hidden='true' />
                    ) : (
                      <Plus className='size-3.5' aria-hidden='true' />
                    )}
                    {busy
                      ? t('stationVolumes.section.overlays.saving')
                      : t('stationVolumes.section.overlays.addOverlay')}
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              {atCap && (
                <TooltipContent data-testid='sv-overlays-cap'>
                  {t('stationVolumes.section.overlays.cap', { max: MAX_SECTION_OVERLAYS })}
                </TooltipContent>
              )}
            </Tooltip>
            <PopoverContent
              align='start'
              side='top'
              data-testid='sv-overlays-picker'
              // Escape unwinds ONE step: out of a file, then out of the
              // palette, then out of the panel. Closing all three on one key
              // would throw away a search the user is halfway through.
              onEscapeKeyDown={(e) => {
                if (!drilled) return;
                e.preventDefault();
                surfaceRoot();
              }}
              className='sv-palette w-90 border-slate-200 bg-white/95 p-0 backdrop-blur dark:border-stone-700 dark:bg-stone-900/95'
            >
              <Command
                // The labels carry the layer's own name, so the project's own
                // vocabulary is what the search matches on.
                className='bg-transparent'
              >
                <CommandInput
                  data-testid='sv-overlays-search'
                  className='text-[11px]'
                  value={query}
                  onValueChange={setQuery}
                  placeholder={t('stationVolumes.section.overlays.searchPlaceholder')}
                />
                <CommandList className='max-h-64'>
                  {(anyCandidate || drilled) && (
                    <CommandEmpty className='py-4 text-[11px]'>
                      {t('stationVolumes.section.overlays.noMatches')}
                    </CommandEmpty>
                  )}
                  {drilled ? (
                    // One file, and only this file: the ONLY place a
                    // `useLandXmlSurfaceNames` is mounted.
                    <CommandGroup heading={drilled.layerName}>
                      <CommandItem
                        data-testid='sv-overlays-drill-back'
                        value={t('stationVolumes.section.overlays.allSurfaces')}
                        onSelect={surfaceRoot}
                        className='gap-1 px-2 py-1.5 text-[11px]'
                      >
                        <ChevronLeft className='size-3.5' aria-hidden='true' />
                        {t('stationVolumes.section.overlays.allSurfaces')}
                      </CommandItem>
                      <LandXmlSurfaceItems
                        layer={layers.find((l) => l.id === drilled.layerId)}
                        layerName={drilled.layerName}
                        addedIds={addedIds}
                        addedText={t('stationVolumes.section.overlays.alreadyAdded')}
                        loadingText={t('stationVolumes.section.overlays.readingSurfaces')}
                        emptyText={t('stationVolumes.section.overlays.noSurfacesInFile')}
                        onPick={add}
                      />
                    </CommandGroup>
                  ) : (
                    <>
                      <CommandGroup heading={t('stationVolumes.section.overlays.groupRuns')}>
                        {runCandidates.length === 0 ? (
                          <div data-testid='sv-overlays-no-runs' className={`px-2 py-1.5 ${MUTED}`}>
                            {t('stationVolumes.section.overlays.noRuns')}
                          </div>
                        ) : (
                          runCandidates.map((c) => (
                            <CandidateItem
                              key={c.id}
                              candidate={c}
                              added={addedIds.has(c.id)}
                              addedText={t('stationVolumes.section.overlays.alreadyAdded')}
                              onPick={add}
                            />
                          ))
                        )}
                      </CommandGroup>
                      <CommandGroup heading={t('stationVolumes.section.overlays.groupSurfaces')}>
                        {surfaces.length === 0 ? (
                          <div data-testid='sv-overlays-no-surfaces' className={`px-2 py-1.5 ${MUTED}`}>
                            {t('stationVolumes.section.overlays.noSurfaces')}
                          </div>
                        ) : (
                          <>
                            {/* A file, not its contents: opening it is what
                                costs the download, so the user asks first. */}
                            {landXmlSurfaces.map((s) => (
                              <CommandItem
                                key={s.layerId}
                                data-testid={`sv-overlay-drill-${s.layerId}`}
                                value={s.layerName}
                                onSelect={() => {
                                  setDrillId(s.layerId);
                                  setQuery('');
                                }}
                                className='gap-2 px-2 py-1.5 text-[11px]'
                              >
                                <span className='min-w-0 flex-1 truncate' title={s.layerName}>
                                  {s.layerName}
                                </span>
                                <ChevronRight className={`size-3.5 shrink-0 ${MUTED}`} aria-hidden='true' />
                              </CommandItem>
                            ))}
                            {plainSurfaceCandidates.map((c) => (
                              <CandidateItem
                                key={c.id}
                                candidate={c}
                                added={addedIds.has(c.id)}
                                addedText={t('stationVolumes.section.overlays.alreadyAdded')}
                                onPick={add}
                              />
                            ))}
                          </>
                        )}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <span
            data-testid='sv-overlays-counter'
            aria-label={t('stationVolumes.section.overlays.countLabel', {
              n: settings.length,
              max: MAX_SECTION_OVERLAYS,
            })}
            className={`sv-num shrink-0 ${MUTED}`}
          >
            {settings.length} / {MAX_SECTION_OVERLAYS}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
};
